import { Component, OnInit } from "@angular/core";
import { PadlockService } from "../../padlock/padlock.service";
import { UserModel } from "./user.model";
import { PasswordUpdateModel } from "./password-update.model";
import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ToastService } from "../../shared/toast.service";
import { NGXLogger } from "ngx-logger";

const passwordMatchValidator: ValidatorFn = (
  formGroup: UntypedFormGroup,
): ValidationErrors | null => {
  if (
    formGroup.get("newPassword").value ===
    formGroup.get("confirmNewPassword").value
  ) {
    return null;
  } else {
    return { passwordMismatch: true };
  }
};

@Component({
  selector: "app-user-profile",
  templateUrl: "./user-profile.component.html",
  styleUrls: ["./user-profile.component.scss"],
  standalone: false,
})
export class UserProfileComponent implements OnInit {
  FIELD_REQUIRED = "Field Required";

  userForm = new UntypedFormGroup({
    emailInput: new UntypedFormControl("", [
      Validators.required,
      Validators.email,
    ]),
  });

  emailInputError = "";

  userPasswordForm = new UntypedFormGroup(
    {
      currentPassword: new UntypedFormControl("", Validators.required),
      newPassword: new UntypedFormControl("", Validators.required),
      confirmNewPassword: new UntypedFormControl("", Validators.required),
    },
    { validators: [passwordMatchValidator] },
  ); // custom match validator

  currentPasswordError: string = this.FIELD_REQUIRED;
  newPasswordError: string = this.FIELD_REQUIRED;
  confirmNewPasswordError: string = this.FIELD_REQUIRED;

  user: UserModel = new UserModel();
  pass: PasswordUpdateModel = new PasswordUpdateModel();
  decodedUserUuid: string;

  hidePassword = true;
  hideNewPassword = true;
  hideConfirmNewPassword = true;

  /* Shorthands for form controls (used from within template) */
  get currentPassword() {
    return this.userPasswordForm.get("currentPassword");
  }
  get newPassword() {
    return this.userPasswordForm.get("newPassword");
  }
  get confirmNewPassword() {
    return this.userPasswordForm.get("confirmNewPassword");
  }

  constructor(
    public padlockService: PadlockService,
    private toast: ToastService,
    private logger: NGXLogger,
  ) {}

  ngOnInit() {
    const decodedToken = this.padlockService.getDecodedToken();
    if (decodedToken) {
      this.decodedUserUuid = JSON.parse(JSON.stringify(decodedToken)).sub;
      this.loadUserProfile(this.decodedUserUuid);
    }
  }

  loadUserProfile(userId: string) {
    this.padlockService.getUserProfile(userId).subscribe((resp: any) => {
      this.user.uuid = resp.uuid;
      this.user.email = resp.email;
    });
  }

  applyChanges(user: UserModel) {
    const body = { email: user.email };

    this.padlockService.updateUserProfile(user.uuid, body).subscribe(() => {
      this.toast.openToast("User Profile Updated");
    });
  }

  applyPasswordChanges() {
    this.padlockService
      .changePassword(
        this.user.uuid,
        this.currentPassword.value,
        this.newPassword.value,
      )
      .subscribe(
        () => {
          this.toast.openToast("Password updated succesfully!");
          this.userPasswordForm.reset();
        },
        (error) => {
          this.logger.debug("Error occurred when changing password:", error);
          this.toast.openErrorToast(`Password update failed`);
        },
      );
  }

  setEmailInputError() {
    const emailInput = this.userForm.get("emailInput");
    this.emailInputError = emailInput.hasError("required")
      ? this.FIELD_REQUIRED
      : emailInput.hasError("email")
        ? "Not a valid email"
        : "";
  }

  setCurrentPasswordError() {
    this.currentPasswordError = this.currentPassword.hasError("required")
      ? this.FIELD_REQUIRED
      : "";
  }

  setNewPasswordError() {
    this.newPasswordError = this.newPassword.hasError("required")
      ? this.FIELD_REQUIRED
      : "";

    if (this.newPassword.touched) {
      this.setConfirmNewPasswordError();
    }
  }

  setConfirmNewPasswordError() {
    this.confirmNewPasswordError = this.confirmNewPassword.hasError("required")
      ? this.FIELD_REQUIRED
      : this.userPasswordForm.hasError("passwordMismatch")
        ? "Passwords do not match"
        : "";

    if (this.userPasswordForm.hasError("passwordMismatch")) {
      this.confirmNewPassword.setErrors({ passwordMismatch: true });
    } else {
      const errors = { ...this.confirmNewPassword.errors };
      delete errors.passwordMismatch;
      this.confirmNewPassword.setErrors(errors);
      this.confirmNewPassword.updateValueAndValidity();
    }
  }
}
